import React, { useEffect, useState } from "react";
import { Box, Card, CardContent, Typography, Chip, Button, Alert, CircularProgress, Paper } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import AdminLayout from "../AdminLayout";
import ev from "../../services/eventV2Service";
import { db } from "../../firebase/config";
import { doc, getDoc } from "firebase/firestore";

export default function EventDetailV2() {
  const { eventId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [credits, setCredits] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [eligibleExternal, setEligibleExternal] = useState(false);
  const [myRegistration, setMyRegistration] = useState(null);

  useEffect(() => {
    const unsub = ev.listenEvent(eventId, (e) => {
      setEvent(e);
      setLoading(false);
    });
    return () => unsub && unsub();
  }, [eventId]);

  useEffect(() => {
    async function loadCredits() {
      if (!user?.uid) return;
      const snap = await getDoc(doc(db, "users", user.uid));
      setCredits(snap.exists() ? (snap.data().totalCredits || 0) : 0);
    }
    loadCredits();
  }, [user]);

  useEffect(() => {
    async function loadEligibility() {
      if (!event || !user?.uid) return;
      if (event.type !== "external") {
        setEligibleExternal(false);
        return;
      }
      try {
        const attemptId = `${eventId}_${user.uid}`;
        const snap = await getDoc(doc(db, "event_quiz_attempts", attemptId));
        const passed = snap.exists() ? !!snap.data().passed : false;
        const score = snap.exists() ? Number(snap.data().score || 0) : 0;
        const cutoff = Number(event.cutoff || 0);
        setEligibleExternal(passed || score >= cutoff);
        const reg = await ev.getUserRegistration(eventId, user.uid);
        setMyRegistration(reg);
      } catch (_) {
        setEligibleExternal(false);
        setMyRegistration(null);
      }
    }
    loadEligibility();
  }, [event, user, eventId]);

  const handleRegister = async () => {
    setError("");
    setSuccess("");
    try {
      await ev.registerForEvent(user.uid, eventId);
      setSuccess("Registered successfully");
    } catch (e) {
      setError(e.message || "Registration failed");
    }
  };

  const canRegisterInternal = () => {
    if (!event) return false;
    return event.type === "internal" && credits >= (event.credit_cap || 0);
  };

  const canAttemptQuiz = () => {
    return event?.type === "external" && !eligibleExternal;
  };
  const canRegisterExternal = () => {
    return event?.type === "external" && eligibleExternal;
  };

  if (loading) {
    return (
      <AdminLayout title="Event">
        <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}><CircularProgress /></Box>
      </AdminLayout>
    );
  }

  if (!event) {
    return (
      <AdminLayout title="Event">
        <Box sx={{ p: 3 }}>
          <Typography>Event not found</Typography>
        </Box>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Event">
      <Box sx={{ p: 3 }}>
        <Card sx={{ borderTop: "6px solid #003366" }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 800, color: "#003366", mb: 1 }}>{event.title}</Typography>
            <Typography color="text.secondary" sx={{ mb: 2 }}>{event.description || "No description"}</Typography>
            <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
              <Chip label={event.type.toUpperCase()} />
              <Chip label={`Date: ${event.date || "TBD"}`} />
              <Chip label={`Venue: ${event.venue}`} />
              <Chip label={`Credit Cap: ${event.credit_cap || 0}`} />
            </Box>

            {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 1 }}>{success}</Alert>}

            {canRegisterInternal() && (
              <Button variant="contained" onClick={handleRegister} sx={{ backgroundColor: "#C1272D", "&:hover": { backgroundColor: "#A51F25" } }}>
                Register
              </Button>
            )}

            {canAttemptQuiz() && (
              <Button variant="contained" onClick={() => navigate(`/student/events-v2/${eventId}/quiz`)} sx={{ ml: 2 }}>
                Attempt Qualification Quiz
              </Button>
            )}
            {canRegisterExternal() && (
              <Button variant="contained" onClick={() => navigate(`/student/events-v2/${eventId}/register`)} sx={{ ml: 2 }}>
                {myRegistration ? "Manage Team" : "Register Team"}
              </Button>
            )}
            {canRegisterExternal() && !!event?.externalRegistrationLink && (
              <Button
                variant="outlined"
                sx={{ ml: 2 }}
                onClick={() => window.open(event.externalRegistrationLink, "_blank", "noopener,noreferrer")}
              >
                Register Now
              </Button>
            )}
            {event?.type === "external" && (
              <Button variant="text" sx={{ ml: 2 }} onClick={() => navigate(`/student/events-v2/${eventId}/timeline`)}>
                Open Workflow Timeline
              </Button>
            )}
            {event?.type === "external" && myRegistration && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  Your Team Members
                </Typography>
                {(myRegistration.team || []).length === 0 ? (
                  <Typography color="text.secondary">No team members added yet.</Typography>
                ) : (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {(myRegistration.team || []).map((m, idx) => (
                      <Paper key={idx} sx={{ p: 1, border: "1px solid #e5e7eb" }}>
                        <Typography>{m.name} • {m.phone} • {m.email}</Typography>
                      </Paper>
                    ))}
                  </Box>
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
    </AdminLayout>
  );
}
